import React, { useState, useEffect } from "react"; // Import useEffect
import {
	Typography,
	Switch,
	FormControlLabel,
	Box,
	Paper,
	CircularProgress,
	Alert,
	Snackbar,
} from "@mui/material";
import GppGoodIcon from "@mui/icons-material/GppGood";

import axios from "../../../api/axios"; // Your axios instance
import useAuth from "../../../hooks/useAuth";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";

const TradingDashboard = () => {
	// Remove isActive state - use accountMode from context
	const [loading, setLoading] = useState(false);
	const [data, setData] = useState(null);
	const [error, setError] = useState("");
	const [showSuccess, setShowSuccess] = useState(false); // For Snackbar
	// Get setRealAccountMode as well
	const { auth, accountMode, setDemoAccountMode, setRealAccountMode } =
		useAuth();
	const axiosPrivate = useAxiosPrivate();

	// Use useEffect to trigger API call when accountMode becomes 'demo'
	useEffect(() => {
		console.log(accountMode);
		if (accountMode === "demo" && auth?.id) {
			const fetchDemoAccountData = async () => {
				setLoading(true);
				setError("");
				try {
					// Consider if this PATCH request should also fetch data
					// or if you need a separate GET request for demo account data.
					const response = await axiosPrivate.patch(
						`/demo-account/${auth?.id}`
					);
					setData(response.data);
					setShowSuccess(true); // Show success snackbar after successful activation and data fetch
				} catch (err) {
					setError("Failed to activate demo trading account or fetch data.");
					// If activation fails, you might want to switch back to real mode
					// setRealAccountMode(); // Uncomment if this is the desired behavior
				} finally {
					setLoading(false);
				}
			};
			fetchDemoAccountData();
		} else if (accountMode === "real") {
			// Reset data and hide success when switching to real mode
			setData(null);
			setShowSuccess(false);
		}
	}, [accountMode, auth?.id, axiosPrivate]); // Depend on accountMode, auth.id, and axiosPrivate

	const handleToggle = () => {
		if (accountMode === "demo") {
			// Switching to real mode
			setRealAccountMode();
		} else {
			// Switching to demo mode
			setDemoAccountMode();
			// The API call will be triggered by the useEffect when accountMode changes to 'demo'
		}
	};

	const handleCloseSnackbar = () => {
		setShowSuccess(false);
	};

	return (
		<Box sx={{ maxWidth: 900, margin: "auto", padding: 2, my: "3rem" }}>
			{/* Toggle Switch */}
			<FormControlLabel
				control={
					<Switch
						checked={accountMode === "demo"} // Check if in demo mode
						onChange={handleToggle}
						color="primary"
						name="activateDemoAccount" // Update name
						disabled={loading} // Disable switch while loading
					/>
				}
				label="Activate Demo Trading Account" // Update label
				sx={{ mb: 3 }}
			/>

			{/* Success Snackbar */}
			<Snackbar
				open={showSuccess}
				autoHideDuration={3000}
				onClose={handleCloseSnackbar}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}>
				<Alert
					severity="success"
					onClose={handleCloseSnackbar}
					sx={{ width: "100%" }}>
					🎉 You're now in demo mode! Practice trading without risks.
				</Alert>
			</Snackbar>

			{/* Content based on accountMode */}
			{accountMode === "demo" ? (
				<>
					<Typography variant="h6" gutterBottom sx={{ color: "green" }}>
						Demo Trading Mode Activated
					</Typography>
					{loading ? (
						<CircularProgress />
					) : error ? (
						<Alert severity="error">{error}</Alert>
					) : data ? (
						// Display demo account specific data here
						<Typography>
							Practice trading without risks. (Data available)
						</Typography>
					) : (
						<Typography>No demo account data available.</Typography>
					)}
				</>
			) : (
				// Content for real mode (or when demo is off)
				<Typography>
					Switch to activate your demo trading account and practice trading
					without risks.
				</Typography>
			)}

			{/* Static info */}
			<Paper sx={{ p: 3 }}>
				<Typography variant="h4" gutterBottom>
					About Demo Trading Accounts
				</Typography>
				<Typography paragraph>
					Practice trading in a risk-free environment with our demo account. No
					real money involved, perfect for learning and testing strategies.
				</Typography>
				<Typography paragraph>
					Toggle the switch above to activate the demo environment and start
					practicing today.
				</Typography>
				{/* <Typography paragraph>
                    To activate your real trading account, toggle the switch at the top of
                    this page. Once activated, you will be able to view your live account
                    details, open positions, and manage your investments.
                </Typography> */}
			</Paper>
		</Box>
	);
};

export default TradingDashboard;
